/**
 * Modal de tratamento de Unpicking — #MT-BCE6550
 *
 * Analista: Aceitar (R→B → Separada), Rejeitar (→ Separando), Substituir (lote/posição).
 * Conferente não usa esta tela na conferência.
 */

import api from '../api.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';
import { UNPICK_REASON_LABELS, UNPICK_STATUS_LABELS } from './outputs-unpick.js';

const HOST = api.HOST;

function escapeHtml(value) {
	if (value == null) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function fmtDate(iso) {
	if (!iso) return '—';
	try {
		return new Date(iso).toLocaleString('pt-BR');
	} catch {
		return iso;
	}
}

function statusBadge(status) {
	const colors = {
		REQUESTED: '#e6a817',
		APPROVED:  '#2a7a3b',
		REJECTED:  '#c0392b',
		CANCELED:  '#888',
		EXECUTED:  '#0e7490',
	};
	const info  = UNPICK_STATUS_LABELS[status] || { label: status };
	const color = colors[status] || '#888';
	return `<span style="display:inline-block;background:${color};color:#fff;border-radius:3px;padding:1px 7px;font-size:0.78rem;font-weight:600">${escapeHtml(info.label || status)}</span>`;
}

function openSubstituteModal(companyId, outputId, gap, onDone) {
	const html = `
		<div class="msgbox" style="min-width:420px;max-width:90vw">
			<div class="mb-title">Substituir item — linha ${escapeHtml(gap.item_number)}</div>
			<div class="mb-text" style="display:flex;flex-direction:column;gap:0.6rem;font-size:0.875rem">
				<p style="margin:0;color:#555">Falta (gap): <strong>${escapeHtml(gap.gap)}</strong> · Lote atual: ${escapeHtml(gap.lot_number || gap.lot_id || '—')}</p>
				<label>Nova posição (location_id)<sup>*</sup>
					<input id="sub-loc" type="number" min="1" step="1" style="width:100%;padding:4px 6px" required>
				</label>
				<label>Novo lote (lot_id)<sup>*</sup>
					<input id="sub-lot" type="number" min="1" step="1" style="width:100%;padding:4px 6px" required>
				</label>
				<label>Quantidade a substituir<sup>*</sup>
					<input id="sub-qty" type="number" min="1" step="1" value="${escapeHtml(gap.gap)}" style="width:100%;padding:4px 6px" required>
				</label>
				<label>Observação
					<textarea id="sub-note" rows="2" style="width:100%;padding:4px 6px;resize:vertical"></textarea>
				</label>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y" style="background:#0e7490;color:#fff;border-color:#0e7490">Confirmar substituição</button>
		</div>
	`;
	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key !== 'Y') return;
		const node = dlg.getNode();
		const payload = {
			output_item_number: gap.item_number,
			new_location_id: parseInt(node.querySelector('#sub-loc').value, 10),
			new_lot_id: parseInt(node.querySelector('#sub-lot').value, 10),
			quantity: parseInt(node.querySelector('#sub-qty').value, 10),
			note: node.querySelector('#sub-note').value.trim() || undefined,
		};
		const btn = node.querySelector('.bn_Y');
		btn.disabled = true;
		btn.textContent = 'Aguarde…';
		const result = await api.call(api.substitute_output_unpick, companyId, outputId, payload);
		if (!result) {
			btn.disabled = false;
			btn.textContent = 'Confirmar substituição';
			return;
		}
		dlg.destroy();
		notify('Substituição registrada. Conferência liberada quando não houver mais falta.', 'Unpicking');
		if (typeof onDone === 'function') onDone();
	}, /Escape|N|Y/);
}

/**
 * Abre o modal de tratamento de unpicking (analista).
 * @param {string} companyId
 * @param {number} outputId
 */
export async function openUnpickRequestsModal(companyId, outputId) {
	const html = `
		<div class="msgbox" style="min-width:720px;max-width:96vw;max-height:90vh;overflow-y:auto">
			<div class="mb-title">Tratamento de Unpicking — Saída #${escapeHtml(outputId)}</div>
			<div class="mb-text" style="overflow-x:auto">
				<p style="font-size:0.85rem;color:#555;margin:0 0 0.75rem">
					Unpicking = falta física. <strong>Aceitar</strong> envia o gap de Reservado para Bloqueado Unpicking (R→B)
					e a saída volta para <strong>Separada (30)</strong> para o conferente. O conferente não trata unpicking.
				</p>
				<div id="unpick-treat-summary">
					<p style="color:#888;margin:0.5rem 0">Carregando…</p>
				</div>
			</div>
			<button type="button" class="button mtl bn_N">Fechar</button>
		</div>
	`;

	const dlg = dialog.create(html, (key) => {
		if (key === 'Escape' || key === 'N') dlg.destroy();
	}, /Escape|N/);

	const node = dlg.getNode();
	const box = node.querySelector('#unpick-treat-summary');

	async function render() {
		box.innerHTML = '<p style="color:#888;margin:0.5rem 0">Carregando…</p>';
		let data;
		try {
			const resp = await fetch(
				`${HOST}/companies/${companyId}/outputs/${outputId}/unpick-treatment`,
				{ method: 'GET', credentials: 'include' }
			);
			if (!resp.ok) {
				const errText = await resp.text();
				throw new Error(errText || `HTTP ${resp.status}`);
			}
			data = await resp.json();
		} catch (err) {
			box.innerHTML = `<p style="color:#c0392b">Erro ao carregar: ${escapeHtml(err.message)}</p>`;
			return;
		}

		const status = data.status;
		const gaps = data.gaps || [];
		const requests = data.requests || [];
		const canTreat = status === 50;

		const actionsBar = canTreat
			? `<div style="display:flex;gap:0.5rem;flex-wrap:wrap;margin:0.75rem 0">
					<button type="button" class="button" id="btn-accept-all"
						style="background:#2a7a3b;color:#fff;border-color:#2a7a3b">Aceitar falta (R→B → Separada)</button>
					<button type="button" class="button" id="btn-reject-all"
						style="background:#c0392b;color:#fff;border-color:#c0392b">Rejeitar (voltar Separando)</button>
				</div>`
			: `<p style="color:#888;font-size:0.85rem;margin:0.5rem 0">
					Status atual: <strong>${escapeHtml(status)}</strong> — ações Aceitar/Rejeitar só em Unpick (50).
				</p>`;

		const gapsHtml = gaps.length
			? `<table style="width:100%;border-collapse:collapse;font-size:0.875rem;margin-bottom:1rem">
					<thead><tr style="background:#f0f0f0">
						<th style="padding:6px 8px;border-bottom:1px solid #ddd">Linha</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd">Produto</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd">Lote</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd;text-align:right">Req.</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd;text-align:right">Forn.</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd;text-align:right">Falta</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd"></th>
					</tr></thead>
					<tbody>${gaps.map((g, idx) => `
						<tr>
							<td style="padding:6px 8px;text-align:center">${escapeHtml(g.item_number)}</td>
							<td style="padding:6px 8px">${escapeHtml(g.sku || g.product_id || '—')}</td>
							<td style="padding:6px 8px">${escapeHtml(g.lot_number || g.lot_id || '—')}</td>
							<td style="padding:6px 8px;text-align:right">${escapeHtml(g.quantity_required)}</td>
							<td style="padding:6px 8px;text-align:right">${escapeHtml(g.quantity_supplied)}</td>
							<td style="padding:6px 8px;text-align:right;font-weight:600;color:#c0392b">${escapeHtml(g.gap)}</td>
							<td style="padding:6px 8px;text-align:right">
								${canTreat ? `<button type="button" class="button btn-sub" data-idx="${idx}"
									style="font-size:0.78rem;padding:2px 8px;background:#0e7490;color:#fff;border-color:#0e7490">Substituir</button>` : ''}
							</td>
						</tr>`).join('')}
					</tbody>
				</table>`
			: '<p style="color:#2a7a3b;margin:0.5rem 0">Nenhuma falta (gap) aberta nesta saída.</p>';

		const reqHtml = requests.length
			? `<h4 style="margin:0.5rem 0;font-size:0.95rem">Solicitações registradas</h4>
				<table style="width:100%;border-collapse:collapse;font-size:0.85rem">
					<thead><tr style="background:#f0f0f0">
						<th style="padding:6px 8px;border-bottom:1px solid #ddd">Linha</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd">Qtd</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd">Motivo</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd">Status</th>
						<th style="padding:6px 8px;border-bottom:1px solid #ddd">Quando</th>
					</tr></thead>
					<tbody>${requests.map((r) => `
						<tr>
							<td style="padding:6px 8px;text-align:center">${escapeHtml(r.output_item_number)}</td>
							<td style="padding:6px 8px;text-align:right">${escapeHtml(r.quantity)}</td>
							<td style="padding:6px 8px">${escapeHtml(UNPICK_REASON_LABELS[r.reason_code] || r.reason_code)}</td>
							<td style="padding:6px 8px">${statusBadge(r.status)}</td>
							<td style="padding:6px 8px;color:#888">${escapeHtml(fmtDate(r.requested_at))}</td>
						</tr>`).join('')}
					</tbody>
				</table>`
			: '';

		box.innerHTML = `
			<p style="margin:0 0 0.5rem;font-size:0.9rem">Status da saída: <strong>${escapeHtml(status)}</strong>
				${data.gaps_blocked ? ' · gaps já cobertos por R→B' : ''}</p>
			${actionsBar}
			${gapsHtml}
			${reqHtml}
		`;

		const btnAccept = box.querySelector('#btn-accept-all');
		if (btnAccept) {
			btnAccept.addEventListener('click', async () => {
				btnAccept.disabled = true;
				btnAccept.textContent = 'Aguarde…';
				const result = await api.call(api.accept_output_unpick, companyId, outputId, {});
				if (!result) {
					btnAccept.disabled = false;
					btnAccept.textContent = 'Aceitar falta (R→B → Separada)';
					return;
				}
				notify(
					`Falta aceita: ${result.quantity_blocked || 0} un. em Bloqueado Unpicking. Saída → Separada (${result.status}).`,
					'Unpicking'
				);
				render();
			});
		}
		const btnReject = box.querySelector('#btn-reject-all');
		if (btnReject) {
			btnReject.addEventListener('click', async () => {
				btnReject.disabled = true;
				btnReject.textContent = 'Aguarde…';
				const result = await api.call(api.treat_reject_output_unpick, companyId, outputId, {});
				if (!result) {
					btnReject.disabled = false;
					btnReject.textContent = 'Rejeitar (voltar Separando)';
					return;
				}
				notify(`Unpicking rejeitado. Saída voltou para Separando (${result.status}).`, 'Unpicking');
				render();
			});
		}
		box.querySelectorAll('.btn-sub').forEach((btn) => {
			btn.addEventListener('click', () => {
				const g = gaps[parseInt(btn.dataset.idx, 10)];
				openSubstituteModal(companyId, outputId, g, render);
			});
		});
	}

	render();
}
